import React, { useEffect, useState } from "react";
import { CircleCheck, SlidersHorizontal, TriangleAlert } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import SectionLabel from "../common/SectionLabel";
import RejectionNotice from "./RejectionNotice";
import { MAX_LEVERAGE } from "./stubData";
import { clamp } from "./format";

/**
 * RiskConfigCard — the engine's REAL per-user risk limits, wired to the
 * existing POST /config endpoint (via the tab's onSave → signalsApi.setConfig).
 * Mirrors the supervised setup flow's controls exactly: these caps are
 * enforced by the backend before any trade is placed.
 *
 * This is deliberately the GLOBAL risk config from the existing API contract
 * ({ maxLeverage, maxCollateralPctPerTrade, pairAllowlist, enabled }) — it is
 * NOT the per-pair allocation engine, which stays stubbed (audit-gated).
 */

// Common gTrade venue pair names offered for the allowlist — same list as the
// supervised setup flow. Not recommendations; selecting one only limits which
// signals are allowed to act.
const COMMON_PAIRS = [
  "BTC/USD",
  "ETH/USD",
  "SOL/USD",
  "ARB/USD",
  "LINK/USD",
  "AVAX/USD",
  "MATIC/USD",
  "DOGE/USD",
];

/* Product ceiling: 2x (MAX_LEVERAGE) — matches the account config, the
   mockup, and PairRow's red ">1x" alert. The API contract technically admits
   higher values, so anything seeded from /status above the ceiling is clamped
   down and can never be re-saved above it from this UI. */
const DEFAULT_CFG = {
  maxLeverage: 1,
  maxCollateralPctPerTrade: 5,
  pairAllowlist: ["BTC/USD", "ETH/USD"],
  enabled: false,
};

/**
 * DOM id of one allowlist chip. The Supernova "Use open slot" cross-link (SUP-6)
 * sends the member straight to the control that turns a pair on — it changes no
 * config itself; the member still selects the pair and presses Save.
 */
export const allowlistChipId = (pair) =>
  `arc-sa-allow-${String(pair || "").toUpperCase().replace(/[^A-Z0-9]/g, "")}`;

/** Fallback scroll target when the pair isn't one of COMMON_PAIRS. */
export const RISK_LIMITS_DOM_ID = "arc-sa-risk-limits";

const RiskConfigCard = ({ riskConfig, onSave, saving, backendReady, rejection }) => {
  const [cfg, setCfg] = useState(DEFAULT_CFG);

  /* seed from the backend's saved config whenever /status delivers one */
  useEffect(() => {
    if (!riskConfig) return;
    setCfg((c) => ({
      maxLeverage: clamp(riskConfig.maxLeverage ?? c.maxLeverage, 1, MAX_LEVERAGE),
      maxCollateralPctPerTrade:
        riskConfig.maxCollateralPctPerTrade ?? c.maxCollateralPctPerTrade,
      pairAllowlist: Array.isArray(riskConfig.pairAllowlist)
        ? riskConfig.pairAllowlist
        : c.pairAllowlist,
      enabled: riskConfig.enabled ?? c.enabled,
    }));
  }, [riskConfig]);

  const togglePair = (pair) =>
    setCfg((c) => ({
      ...c,
      pairAllowlist: c.pairAllowlist.includes(pair)
        ? c.pairAllowlist.filter((p) => p !== pair)
        : [...c.pairAllowlist, pair],
    }));

  return (
    <Card hover={false} as="section" aria-label="Risk limits" id={RISK_LIMITS_DOM_ID}>
      <div className="flex items-center gap-2 mb-1">
        <SlidersHorizontal size={17} className="text-arc-accent-ink" aria-hidden="true" />
        <h3 className="text-h2 text-arc-ink">Your risk limits</h3>
      </div>
      <p className="text-body text-arc-ink-2 mb-4 max-w-2xl">
        Saved to the engine and enforced server-side before any trade is placed.
      </p>

      {/* leverage warning — required on this screen */}
      <div className="flex items-start gap-2.5 rounded-arc-ctl bg-arc-warn-soft p-3 mb-4">
        <TriangleAlert size={15} className="text-arc-warn flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-caption text-arc-warn leading-relaxed">
          Higher leverage means a smaller price move can liquidate your
          collateral. Set limits you understand and can afford to lose entirely.
          These settings cap risk — they do not guarantee any outcome.
        </p>
      </div>

      <div className="space-y-5">
        {/* max leverage */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <SectionLabel>Max leverage per trade</SectionLabel>
            <span className="tnum text-sm font-semibold text-arc-accent-ink">
              {Number(cfg.maxLeverage).toFixed(1)}x
            </span>
          </div>
          <input
            type="range"
            min="1"
            max={MAX_LEVERAGE}
            step="0.1"
            value={cfg.maxLeverage}
            onChange={(e) =>
              setCfg((c) => ({
                ...c,
                maxLeverage: clamp(Number(e.target.value) || 1, 1, MAX_LEVERAGE),
              }))
            }
            className="w-full accent-arc-accent"
            aria-label="Max leverage per trade"
          />
          <p className="text-caption text-arc-ink-4 mt-1">max {MAX_LEVERAGE}x</p>
        </div>

        {/* max collateral % per trade */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <SectionLabel>Max collateral per trade</SectionLabel>
            <span className="tnum text-sm font-semibold text-arc-accent-ink">
              {cfg.maxCollateralPctPerTrade}%
            </span>
          </div>
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={cfg.maxCollateralPctPerTrade}
            onChange={(e) =>
              setCfg((c) => ({ ...c, maxCollateralPctPerTrade: Number(e.target.value) }))
            }
            className="w-full accent-arc-accent"
            aria-label="Max collateral per trade"
          />
        </div>

        {/* pair allowlist */}
        <div>
          <SectionLabel className="mb-1.5">Pair allowlist</SectionLabel>
          <p className="text-caption text-arc-ink-3 mb-2">
            Only signals for the pairs you select are allowed to act. Unselect
            everything to block all trading.
          </p>
          <div className="flex flex-wrap gap-2">
            {COMMON_PAIRS.map((pair) => {
              const on = cfg.pairAllowlist.includes(pair);
              return (
                <button
                  key={pair}
                  id={allowlistChipId(pair)}
                  type="button"
                  onClick={() => togglePair(pair)}
                  aria-pressed={on}
                  className={[
                    "px-3 py-1.5 rounded-arc-ctl text-xs tnum ring-1 transition",
                    on
                      ? "ring-arc-accent bg-arc-accent-soft text-arc-ink font-semibold"
                      : "ring-arc-line text-arc-ink-3 hover:text-arc-ink-2",
                  ].join(" ")}
                >
                  {pair}
                </button>
              );
            })}
          </div>
        </div>

        {/* master enable */}
        <div className="flex items-center justify-between rounded-arc-ctl ring-1 ring-arc-line p-3">
          <div>
            <p className="text-sm font-medium text-arc-ink">Master enable</p>
            <p className="text-caption text-arc-ink-3">
              When off, no signal will ever place a trade, regardless of the above.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCfg((c) => ({ ...c, enabled: !c.enabled }))}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              cfg.enabled ? "bg-arc-accent" : "bg-arc-paper-2 ring-1 ring-arc-line"
            }`}
            aria-pressed={cfg.enabled}
            aria-label="Master enable"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                cfg.enabled ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        {/* ARC-54 — the engine's 400 for THIS save, next to the controls that
            caused it. The tab-level banner still carries it too; this is about
            a member looking at the sliders seeing that the button they pressed
            was refused, without scrolling to the top of a long tab. */}
        <RejectionNotice rejection={rejection} testId="sa-risk-rejection" />

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={() =>
              // final clamp so no state path can ever post above the ceiling
              onSave({ ...cfg, maxLeverage: clamp(cfg.maxLeverage, 1, MAX_LEVERAGE) })
            }
            disabled={saving || !backendReady}
          >
            <CircleCheck size={15} aria-hidden="true" />
            {saving ? "Saving…" : "Save risk settings"}
          </Button>
          {!backendReady && (
            <span className="text-caption text-arc-ink-3">
              Backend not connected in this environment.
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

export default RiskConfigCard;
